import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import axios from '../lib/axios';

/**
 * Public "reset password" page. Reads a one-time token from the URL query,
 * collects a new password, and submits it to /api/auth/reset-password.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Redirect to login shortly after a successful reset.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => navigate('/login', { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordFieldClasses =
    'w-full pl-11 pr-11 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/25 focus:border-brand-primary transition-all duration-200 placeholder:text-gray-400';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-dark via-brand-primary/90 to-brand-secondary relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-secondary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-sm mx-4 relative z-10 animate-fade-in-up">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/20 border border-white/20 p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-brand-light to-white rounded-2xl flex items-center justify-center shadow-lg shadow-brand-primary/10 mb-4">
              <img src="/logo.png" alt="The Golf Buggy Guy" className="h-14 w-auto object-contain" />
            </div>
            <h1 className="text-xl font-bold text-brand-dark tracking-tight">Set a new password</h1>
            <p className="text-sm text-brand-neutral mt-1">Choose a strong password you&rsquo;ll remember</p>
          </div>

          {done ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="w-12 h-12 text-brand-secondary" />
              </div>
              <p className="text-sm text-brand-dark leading-relaxed">
                Your password has been reset. Redirecting you to sign in&hellip;
              </p>
              <Link
                to="/login"
                className="inline-block mt-4 text-sm font-medium text-brand-primary hover:text-brand-secondary transition-colors"
              >
                Go to sign in now
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="w-12 h-12 text-amber-500" />
              </div>
              <p className="text-sm text-brand-dark leading-relaxed">
                This reset link is missing its token. Please request a new link.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block mt-4 text-sm font-medium text-brand-primary hover:text-brand-secondary transition-colors"
              >
                Request a new link
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center animate-scale-in">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-2">New password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral group-focus-within:text-brand-primary transition-colors" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={passwordFieldClasses}
                      placeholder="At least 8 characters"
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-brand-neutral hover:text-brand-primary transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-2">Confirm password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral group-focus-within:text-brand-primary transition-colors" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className={passwordFieldClasses}
                      placeholder="Re-enter your new password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg hover:shadow-brand-primary/25 hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 shadow-md disabled:opacity-50 disabled:hover:scale-100 mt-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
              </form>

              <div className="mt-6 flex justify-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-brand-primary hover:text-brand-secondary transition-colors"
                >
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-white/40 mt-6">
          The Golf Buggy Guy © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
